import React, { useEffect, useRef, useState } from 'react';
import { ChevronDown, Download, FileSpreadsheet } from 'lucide-react';
import { allDatasets, abcEventsData, callsData, catalogData, ordersData, saveDataset, summaryData, timelineData, workItemsData } from '../exportData';
import { buildCatalog } from '../mockData';
import { downloadExport } from '../api';
import { useReport } from '../ReportContext';
import { toastMessage } from '@/utils/toast-message';
import type { CallRow, Filters, WorkRow } from '../types';

interface Props { tab: string; tabLabel: string; rows: WorkRow[]; calls: CallRow[]; filters: Filters }

/**
 * Every number can be downloaded: the table on screen, and each set of records behind it.
 * Files are CSV with a byte-order mark, so Excel opens them directly. Active filters are
 * applied and written into the file name.
 */
const DownloadMenu: React.FC<Props> = ({ tab, tabLabel, rows, calls, filters }) => {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const { mode, agentId } = useReport();
  const live = mode === 'live';
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [open]);

  // Live: the server builds the file from the same queries as the screen. Sample: generated here.
  const fetchOne = async (dataset: string) => {
    setBusy(true);
    try {
      const n = await downloadExport(dataset, filters, agentId);
      toastMessage(`Downloaded ${n.toLocaleString('en-IN')} rows (${dataset.replace('_', ' ')})`, 'success');
    } catch { toastMessage('The download failed', 'error'); } finally { setBusy(false); }
  };
  const run = (sample: () => void, dataset: string) => () => { setOpen(false); if (live) void fetchOne(dataset); else sample(); };
  const everything = () => {
    setOpen(false);
    if (live) {
      ['summary', 'work_items', 'abc_events', 'timeline', 'calls', 'orders', 'category'].forEach((d, i) => window.setTimeout(() => { void fetchOne(d); }, i * 700));
      return;
    }
    allDatasets(rows, filters, calls).forEach((ds, i) => window.setTimeout(() => saveDataset(ds, filters), i * 450));
  };

  const item = 'flex w-full items-start gap-2 px-3 py-2 text-left text-sm text-slate-700 hover:bg-violet-50 dark:text-slate-200 dark:hover:bg-slate-700';
  return (
    <div className="relative" ref={ref}>
      <button type="button" onClick={() => setOpen(o => !o)} aria-haspopup="menu" aria-expanded={open}
        className="flex items-center gap-1.5 rounded-lg bg-violet-100 px-3 py-1.5 text-sm font-medium text-violet-800 hover:bg-violet-200 dark:bg-violet-400/20 dark:text-violet-100">
        <Download className="h-4 w-4" /> {busy ? 'Preparing…' : 'Download data'} <ChevronDown className="h-3.5 w-3.5" />
      </button>
      {open && (
        <div role="menu" className="absolute right-0 z-30 mt-1 w-80 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-xl dark:border-slate-700 dark:bg-slate-800">
          <p className="border-b border-slate-100 bg-slate-50 px-3 py-2 text-[11px] text-slate-700 dark:border-slate-700 dark:bg-slate-900/40">
            CSV, opens in Excel. Uses the filters above ({filters.from} to {filters.to}).
          </p>
          <button role="menuitem" className={item} onClick={run(() => saveDataset(summaryData(tab, rows, filters, calls), filters), tab === 'category' ? 'category' : 'summary')}>
            <FileSpreadsheet className="mt-0.5 h-4 w-4 shrink-0 text-violet-500" />
            <span><b className="font-medium">This tab: {tabLabel}</b><br /><span className="text-xs text-slate-600">The table as shown, with totals</span></span>
          </button>
          <div className="border-t border-slate-100 px-3 pb-1 pt-2 text-[11px] font-semibold uppercase tracking-wide text-slate-600 dark:border-slate-700">Records behind the numbers</div>
          <button role="menuitem" className={item} onClick={run(() => saveDataset(workItemsData(rows), filters), 'work_items')}>
            <FileSpreadsheet className="mt-0.5 h-4 w-4 shrink-0 text-emerald-500" />
            <span><b className="font-medium">Work items</b><br /><span className="text-xs text-slate-600">One row per work item (ABC: per customer), every disposition update in numbered columns</span></span>
          </button>
          <button role="menuitem" className={item} onClick={run(() => saveDataset(abcEventsData(rows), filters), 'abc_events')}>
            <FileSpreadsheet className="mt-0.5 h-4 w-4 shrink-0 text-violet-500" />
            <span><b className="font-medium">ABC events</b><br /><span className="text-xs text-slate-600">One row per cart event, with the update that covered it</span></span>
          </button>
          <button role="menuitem" className={item} onClick={run(() => saveDataset(timelineData(rows), filters), 'timeline')}>
            <FileSpreadsheet className="mt-0.5 h-4 w-4 shrink-0 text-fuchsia-500" />
            <span><b className="font-medium">Customer timeline</b><br /><span className="text-xs text-slate-600">Every update, call, remark and owner change, oldest first, latest flagged</span></span>
          </button>
          <button role="menuitem" className={item} onClick={run(() => saveDataset(callsData(calls), filters), 'calls')}>
            <FileSpreadsheet className="mt-0.5 h-4 w-4 shrink-0 text-teal-500" />
            <span><b className="font-medium">Calls</b><br /><span className="text-xs text-slate-600">One row per dial: outcome, talk time, attempt number</span></span>
          </button>
          <button role="menuitem" className={item} onClick={run(() => saveDataset(ordersData(rows), filters), 'orders')}>
            <FileSpreadsheet className="mt-0.5 h-4 w-4 shrink-0 text-sky-500" />
            <span><b className="font-medium">Orders</b><br /><span className="text-xs text-slate-600">Every order with agent, bucket, attribution, status and value</span></span>
          </button>
          <button role="menuitem" className={item} onClick={run(() => saveDataset(catalogData(buildCatalog(rows, filters.category, filters.product)), filters), 'category')}>
            <FileSpreadsheet className="mt-0.5 h-4 w-4 shrink-0 text-orange-500" />
            <span><b className="font-medium">Category › Product › Agent</b><br /><span className="text-xs text-slate-600">One row per agent per product</span></span>
          </button>
          <button role="menuitem" onClick={everything}
            className="flex w-full items-center gap-2 border-t border-slate-100 bg-emerald-50 px-3 py-2.5 text-left text-sm font-medium text-emerald-800 hover:bg-emerald-100 dark:border-slate-700 dark:bg-emerald-400/10 dark:text-emerald-200">
            <Download className="h-4 w-4" /> Download everything (7 files)
          </button>
        </div>
      )}
    </div>
  );
};

export default DownloadMenu;
